import { HydrateOption } from '@core/enums';
import { withQueryProvider } from '@hoc/withQueryProvider';
import withWidget from '@hoc/withWidget';
import useSiteSearch, { OrderType, SearchType } from '@hooks/useSiteSearch';
import { ReactComponent as ChevronDown } from '@img/icons/chevron-down.svg';
import { ReactComponent as ChevronUp } from '@img/icons/chevron-up.svg';
import { ReactComponent as SearchIcon } from '@img/icons/search.svg';
import Button, { LinkButton } from '@stories/Components/Buttons/Button/Button';
import DocumentCard from '@stories/Components/Cards/DocumentCard/DocumentCard';
import { WYSIWYGStyles } from '@stories/Components/Content/WYSIWYG/WYSIWYG.styles';
import Filter from '@stories/Components/Filters/Filter/Filter';
import { ThemeWrapper } from '@helpers/theme';
import * as React from 'react';
import { ArrayParam, NumberParam, StringParam, useQueryParams, withDefault, } from 'use-query-params';
import S from './ProductListing.styles';
const ProductListing = (props) => {
    const [viewAll, setViewAll] = React.useState(false);
    const resultsRef = React.useRef(null);
    const [currentPageIndex, setCurrentPageIndex] = React.useState(1);
    const [params, setParams] = useQueryParams({
        searchTerm: withDefault(StringParam, ''),
        pageIndex: withDefault(NumberParam, 1),
        selectedCategories: withDefault(ArrayParam, []),
    });
    const [isInitialRender, setIsInitialRender] = React.useState(true);
    const [disableRequest, setDisableRequest] = React.useState(true);
    React.useEffect(() => {
        if (isInitialRender) {
            setIsInitialRender(false);
            return;
        }
        setDisableRequest(false);
    }, [JSON.stringify(params)]);
    const search = useSiteSearch({
        request: {
            siteName: props.siteName,
            culture: props.culture,
            searchTerm: params.searchTerm ? params.searchTerm : '',
            searchType: SearchType.Products,
            applyFuzzy: false,
            searchSynonyms: false,
            currentPageIndex: currentPageIndex,
            pageIndex: params.pageIndex ? params.pageIndex : 1,
            pageSize: props.pageSize,
            resultsOrder: params.searchTerm.length > 0 ? OrderType.Relevance : OrderType.NodeOrder,
            searchPath: props.searchPath,
            selectedFilters: params.selectedCategories,
            isProductListing: true,
            documentId: props.documentId,
            matchOnAllSameCategoryItems: false,
        },
        disableRequest,
        initialData: props.initialData,
    });
    const parsedFilters = React.useMemo(() => {
        const mappedFilters = params.selectedCategories.reduce((acc, cur) => {
            const split = cur.split('|');
            if (acc[split[0]]) {
                acc[split[0]].push(split[1]);
            }
            else {
                acc[split[0]] = [split[1]];
            }
            return acc;
        }, {});
        return mappedFilters;
    }, [params.selectedCategories.length]);
    return (React.createElement(ThemeWrapper, { "data-theme": props.theme },
        React.createElement(S.Container, null,
            React.createElement(S.ContentGrid, null,
                props.wysiwygContent && (React.createElement(S.ContentWrapper, null,
                    React.createElement(S.ContentSection, null,
                        React.createElement(WYSIWYGStyles.Wrapper, { dangerouslySetInnerHTML: { __html: props.wysiwygContent } }),
                        props.wysiwygAdditionalContent && (React.createElement(WYSIWYGStyles.Wrapper, { dangerouslySetInnerHTML: { __html: props.wysiwygAdditionalContent }, "data-hidden": !viewAll }))),
                    props.wysiwygAdditionalContent && (React.createElement(Button, { branding: "textOnlyPrimary", title: viewAll ? props.translations['gf.showless'] : props.translations['gf.showmore'], onClick: () => setViewAll(!viewAll), addon: viewAll ? React.createElement(ChevronUp, null) : React.createElement(ChevronDown, null), addonPosition: "right", hideTextMobile: false })))),
                React.createElement(S.Search, null,
                    React.createElement(S.SearchWrapper, null,
                        React.createElement(S.Searchbox, { placeholder: props.searchPlaceholder || undefined, onChange: (e) => {
                                setCurrentPageIndex(params.pageIndex);
                                setParams({ searchTerm: e.currentTarget.value, pageIndex: 1 }, 'pushIn');
                            }, onKeyDown: (e) => {
                                if (e.key.toLowerCase() === 'enter') {
                                    resultsRef.current?.scrollIntoView({ behavior: 'smooth' });
                                }
                            } }),
                        React.createElement(SearchIcon, { onClick: () => resultsRef.current?.scrollIntoView({ behavior: 'smooth' }) }))),
                React.createElement(S.ResultGrid, null,
                    props.filters.items && props.filters?.items?.length > 0 && (React.createElement(S.FilterWrapper, null,
                        React.createElement(Filter, { ...props.filters, translations: props.translations, value: parsedFilters, onChange: (name, value) => {
                                const filterValues = params.selectedCategories;
                                const itemValue = `${name}|${value}`;
                                const itemIndex = filterValues.indexOf(itemValue);
                                if (itemIndex > -1) {
                                    filterValues.splice(itemIndex, 1);
                                }
                                else {
                                    filterValues.push(itemValue);
                                }
                                setCurrentPageIndex(params.pageIndex);
                                setParams({ selectedCategories: filterValues, pageIndex: 1 }, 'pushIn');
                            }, onReset: () => {
                                setCurrentPageIndex(params.pageIndex);
                                setParams({ selectedCategories: [], pageIndex: 1 }, 'pushIn');
                            } }))),
                    React.createElement(S.Results, { ref: resultsRef }, search.response?.results?.length ? (renderItems(search.response.results)) : (React.createElement(S.NoResultsMsg, { dangerouslySetInnerHTML: { __html: props.translations['gf.noresults'] } })))),
                search.response?.canLoadMore ? (React.createElement(S.ResultsFooter, null,
                    React.createElement(LinkButton, { href: `?pageIndex=${params.pageIndex + 1}`, branding: "primaryAlt", title: props.translations['gf.loadmore'], onClick: (e) => {
                            e.preventDefault();
                            if (!params.pageIndex) {
                                setParams({ pageIndex: 1 }, 'pushIn');
                            }
                            else {
                                setCurrentPageIndex(params.pageIndex);
                                setParams({ pageIndex: params.pageIndex + 1 }, 'pushIn');
                            }
                        } }))) : null))));
    function renderItems(items) {
        if (items === undefined) {
            return React.createElement(React.Fragment, null);
        }
        return (React.createElement(React.Fragment, null, items?.map((x, i) => (React.createElement(DocumentCard, { theme: props.theme, key: `${x.documentId}|${i}`, document: {
                title: x.documentName,
                description: x.summaryText,
                imageUrl: x.imageUrl,
                url: x.pageUrl,
                noticeTag: null,
                fullPaddingTop: true,
            } })))));
    }
};
export default withWidget(withQueryProvider(ProductListing), 'ProductListing', {
    hydrate: HydrateOption.InView,
});
